import {
  Home,
  FileText,
  Layers,
  UserPlus,
  Wallet,
  CalendarClock,
  Percent,
  ListChecks,
  Target,
  ShieldAlert,
  Gavel,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  LegalPage,
  LegalSection,
  LegalHeading,
  LegalList,
} from "@/components/blocks/LegalPage";
import { COMPANY } from "@/lib/legal";
import {
  PLANS,
  COMMISSION_PERCENT,
  FOUNDER_MONTHS,
  FOUNDER_UNTIL,
  EXTRA_SPONSORSHIP_FEE,
  TRIAL_DAYS,
  formatPrice,
} from "@/lib/pricing";
import { STATUS_LAUNCH } from "@/lib/status";
import { CLUB_FEATURES, COMPANY_FEATURES } from "@/lib/features";

const [CLUB_PLAN, COMPANY_PLAN] = PLANS;

const SIDEBAR_ITEMS = [
  { id: "top", label: "Start", icon: Home },
  { id: "geltung", label: "Geltungsbereich", icon: FileText },
  { id: "leistung", label: "Leistungen", icon: Layers },
  { id: "konto", label: "Nutzerkonto", icon: UserPlus },
  { id: "preise", label: "Preise", icon: Wallet },
  { id: "laufzeit", label: "Laufzeit", icon: CalendarClock },
  { id: "provision", label: "Provision", icon: Percent },
  { id: "pflichten", label: "Pflichten", icon: ListChecks },
  { id: "ranking", label: "MatchScore", icon: Target },
  { id: "haftung", label: "Haftung", icon: ShieldAlert },
  { id: "schluss", label: "Schluss", icon: Gavel },
];

const linkClass =
  "text-white/80 underline decoration-white/25 underline-offset-4 hover:text-white hover:decoration-brand-orange transition-colors duration-300";

const preisZeile = (plan, prices) =>
  `${plan.name}: ${formatPrice(prices.monthly)} EUR monatlich oder ${formatPrice(
    prices.yearly
  )} EUR jährlich`;

export default function Terms() {
  return (
    <LegalPage
      eyebrow="Rechtliches"
      title="AGB"
      lead="Allgemeine Geschäftsbedingungen für die Nutzung von MatchGate durch Sportvereine und Unternehmen."
      notice={{
        title: "Gilt ab dem Start der Plattform.",
        text: `MatchGate startet ${STATUS_LAUNCH.in}. Derzeit kann noch kein Vertrag geschlossen werden, und bis zum Start können sich diese Bedingungen noch ändern.`,
      }}
      sidebarItems={SIDEBAR_ITEMS}
    >
      <LegalSection id="geltung" title="§ 1 Geltungsbereich und Vertragspartner">
        <p>
          Diese Bedingungen gelten für die Nutzung der Plattform MatchGate.
          Anbieterin und Vertragspartnerin ist die {COMPANY.name},{" "}
          {COMPANY.street}, {COMPANY.postalCode} {COMPANY.city} (nachfolgend
          „MatchGate").
        </p>
        <p>
          Das Angebot richtet sich an Sportvereine, Verbände, Unternehmen und
          sonstige Organisationen, nicht an Verbraucherinnen und Verbraucher im
          Sinne des § 13 BGB. Ein Vertragsschluss mit Verbraucherinnen und
          Verbrauchern kommt nicht zustande. Ein Widerrufsrecht besteht daher
          nicht.
        </p>
        <p>
          Abweichende oder ergänzende Bedingungen der Nutzer werden nicht
          Vertragsbestandteil, auch wenn MatchGate ihnen nicht ausdrücklich
          widerspricht.
        </p>
      </LegalSection>

      <LegalSection id="leistung" title="§ 2 Leistungen von MatchGate">
        <p>
          MatchGate stellt eine Plattform bereit, über die Sportvereine und
          Unternehmen für Sponsoring-Partnerschaften zueinander finden und
          laufende Partnerschaften auswerten können.
        </p>

        <LegalHeading>Für Sportvereine</LegalHeading>
        <LegalList items={CLUB_FEATURES.map((f) => `${f.title}: ${f.short}`)} />

        <LegalHeading>Für Unternehmen</LegalHeading>
        <LegalList
          items={COMPANY_FEATURES.map((f) => `${f.title}: ${f.short}`)}
        />

        <p>
          Bei Abschluss eines Sponsorings richten Verein und Unternehmen es auf
          der Plattform ein. Dabei halten sie die vereinbarten Leistungen fest,
          auch solche, die in keiner Vorlage stehen, und bestätigen die
          Einrichtung beide. Leistungen, die sich messen lassen, wertet
          MatchGate aus; alle übrigen werden von den Nutzern eingetragen und
          belegt.
        </p>
        <p>
          MatchGate wird nicht Partei des Sponsoringvertrags zwischen Verein und
          Unternehmen. Zustandekommen, Inhalt und Erfüllung dieses Vertrags
          liegen ausschließlich bei den beteiligten Parteien. MatchGate schuldet
          keinen Vermittlungserfolg.
        </p>
        <p>
          MatchGate darf den Leistungsumfang weiterentwickeln. Wesentliche
          Einschränkungen bestehender Funktionen kündigen wir mindestens 30
          Tage im Voraus per E-Mail an.
        </p>
      </LegalSection>

      <LegalSection id="konto" title="§ 3 Registrierung und Nutzerkonto">
        <p>
          Die Nutzung setzt ein Konto voraus. Bei der Registrierung sind
          vollständige und zutreffende Angaben zu machen und bei Änderungen zu
          aktualisieren. Wer ein Konto für eine Organisation anlegt, versichert,
          zu deren Vertretung berechtigt zu sein.
        </p>
        <p>
          MatchGate prüft die Berechtigung vor der Freischaltung, etwa anhand
          des Vereinsregisters, einer E-Mail-Adresse der Organisation oder
          durch Rückfrage, und darf ein Konto bis zum Nachweis vorläufig
          sperren.
        </p>
        <p>
          Zugangsdaten sind vertraulich zu behandeln. Bei Verdacht auf unbefugte
          Nutzung ist MatchGate unverzüglich zu informieren.
        </p>
      </LegalSection>

      <LegalSection id="preise" title="§ 4 Preise und Zahlung">
        <p>Die Nutzung ist kostenpflichtig. Es gelten folgende Preise:</p>
        <LegalList
          items={[
            preisZeile(CLUB_PLAN, CLUB_PLAN.regular),
            `${preisZeile(COMPANY_PLAN, COMPANY_PLAN.regular)}, einschließlich eines laufenden Sponsorings`,
          ]}
        />
        <p>
          Alle Beträge sind Endpreise. Gemäß § 19 UStG wird keine Umsatzsteuer
          berechnet und daher auch nicht ausgewiesen.
        </p>

        <LegalHeading>Sponsorings im Unternehmens-Abo</LegalHeading>
        <p>
          Im Unternehmens-Abo ist ein laufendes Sponsoring enthalten. Endet es,
          kann ein neues Sponsoring ohne zusätzliche Gebühr eingerichtet
          werden. Für jedes weitere gleichzeitig laufende Sponsoring fällt bei
          dessen Einrichtung eine einmalige Gebühr von {EXTRA_SPONSORSHIP_FEE}{" "}
          EUR an; sie gilt für die vereinbarte Laufzeit des Sponsorings. Das
          gilt auch für bestehende Sponsorings, die auf die Plattform übertragen
          werden. Wird ein Sponsoring mit demselben Partner verlängert, fällt
          die Gebühr nicht erneut an. Für Vereine fallen keine Gebühren je
          Sponsoring an.
        </p>

        <LegalHeading>Gründerpreis</LegalHeading>
        <p>
          Für Verträge, die bis zum {FOUNDER_UNTIL} geschlossen werden, gelten
          für die ersten {FOUNDER_MONTHS} Monate ab Vertragsschluss folgende
          Gründerpreise:
        </p>
        <LegalList
          items={[
            preisZeile(CLUB_PLAN, CLUB_PLAN.founder),
            preisZeile(COMPANY_PLAN, COMPANY_PLAN.founder),
          ]}
        />
        <p>
          Danach gelten die regulären Preise. Die einmalige Gebühr für weitere
          Sponsorings bleibt vom Gründerpreis unberührt.
        </p>

        <LegalHeading>Zahlungsweise</LegalHeading>
        <p>
          Die Zahlung erfolgt per Rechnung oder SEPA-Lastschrift; maßgeblich
          sind die bei der Bestellung angebotenen Zahlungsarten. Das Entgelt
          ist bei Monatsabos zu Beginn des jeweiligen
          Abrechnungsmonats, bei Jahresabos zu Beginn der Laufzeit fällig.
        </p>

        <LegalHeading>Zahlungsverzug</LegalHeading>
        <p>
          Bleibt eine Zahlung aus, erinnert MatchGate drei Tage nach Fälligkeit
          und erneut nach zehn Tagen. Ist die Zahlung 14 Tage nach Fälligkeit
          nicht eingegangen, kann MatchGate das Bearbeiten und Einrichten
          einschränken. Auswertungen zu laufenden Sponsorings bleiben lesbar,
          damit die andere Seite nicht mitbetroffen ist.
        </p>

        <LegalHeading>Preisänderungen</LegalHeading>
        <p>
          Änderungen der Entgelte kündigt MatchGate mindestens sechs Wochen vor
          Wirksamwerden per E-Mail an. Nutzer können den Vertrag in diesem Fall zum Zeitpunkt des
          Wirksamwerdens außerordentlich kündigen.
        </p>
      </LegalSection>

      <LegalSection id="laufzeit" title="§ 5 Laufzeit und Kündigung">
        <p>
          <strong className="text-white/85">Monatliches Abo:</strong> jederzeit
          kündbar zum Ende des laufenden Abrechnungsmonats.
        </p>
        <p>
          <strong className="text-white/85">Jahresabo:</strong> Laufzeit zwölf
          Monate. Es verlängert sich automatisch um jeweils weitere zwölf
          Monate, sofern es nicht mit einer Frist von einem Monat zum Ende der
          jeweiligen Laufzeit gekündigt wird.
        </p>
        <p>
          Die Kündigung erfolgt über die entsprechende Schaltfläche im
          Nutzerkonto oder formlos per E-Mail. Das Recht zur außerordentlichen
          Kündigung aus wichtigem Grund bleibt für beide Seiten unberührt.
        </p>
        <p>
          Eine Kündigung durch MatchGate ohne wichtigen Grund erfolgt mit einer
          Frist von 30 Tagen unter Angabe der Gründe in Textform.
        </p>

        <LegalHeading>Testphase</LegalHeading>
        <p>
          Neue Nutzer können MatchGate {TRIAL_DAYS} Tage kostenlos testen, ohne
          Zahlungsdaten anzugeben. Wird danach kein Abo abgeschlossen, endet der
          Zugang automatisch; es entstehen keine Kosten.
        </p>

        <LegalHeading>Nach Vertragsende</LegalHeading>
        <p>
          Profile und Projekte werden nach Vertragsende nicht mehr öffentlich
          angezeigt. Auswertungen zu laufenden Sponsorings bleiben drei Monate
          abrufbar und exportierbar.
          Anschließend werden die Daten gelöscht, soweit keine gesetzlichen
          Aufbewahrungspflichten bestehen.
        </p>
      </LegalSection>

      <LegalSection
        id="provision"
        title={`§ 6 Vermittlungsprovision (${COMMISSION_PERCENT} %)`}
      >
        <p>
          Kommt über MatchGate ein Sponsoringvertrag zwischen einem Verein und
          einem Unternehmen zustande, fällt eine Vermittlungsprovision in Höhe
          von {COMMISSION_PERCENT} % an. Ohne Vertragsschluss entsteht keine
          Provision; das Abo bleibt davon unberührt.
        </p>

        <LegalHeading>Schuldner, Bemessung und Fälligkeit</LegalHeading>
        <p>
          Wer die Provision schuldet, wie sie bemessen wird, wann sie fällig
          ist und was gilt, wenn ein Sponsoring rückabgewickelt oder erheblich
          gekürzt wird, legt MatchGate vor dem Start der Plattform in diesen
          Bedingungen fest.
        </p>

        <LegalHeading>Nachweis und Umgehungsschutz</LegalHeading>
        <p>
          Die Parteien sind verpflichtet, einen über MatchGate angebahnten
          Vertragsschluss über die Plattform anzuzeigen. Als Anzeige gilt die
          von beiden Seiten bestätigte Einrichtung des Sponsorings auf der
          Plattform. Die Provision fällt auch an, wenn innerhalb von zwölf
          Monaten nach dem über MatchGate hergestellten Erstkontakt außerhalb
          der Plattform abgeschlossen wird.
        </p>

        <LegalHeading>Bestehende Sponsorings</LegalHeading>
        <p>
          Keine Provision fällt an für Sponsorings, die bereits vereinbart
          waren, bevor Verein und Unternehmen über MatchGate in Kontakt gekommen
          sind, und die nachträglich auf der Plattform eingerichtet werden.
          Beide Parteien bestätigen dies bei der Einrichtung; MatchGate kann
          einen Nachweis verlangen, etwa die bisherige Vereinbarung. Das gilt
          auch für spätere Verlängerungen dieser Sponsorings. Abo und Gebühren
          für weitere Sponsorings (§ 4) bleiben unberührt.
        </p>

      </LegalSection>

      <LegalSection id="pflichten" title="§ 7 Pflichten und Inhalte der Nutzer">
        <p>
          Nutzer stellen Profile, Projekte und Nachrichten in eigener
          Verantwortung ein. Sie sichern zu, dass die Angaben zutreffend sind
          und keine Rechte Dritter verletzen, insbesondere keine Urheber-,
          Marken- oder Persönlichkeitsrechte.
        </p>
        <p>Unzulässig sind insbesondere:</p>
        <LegalList
          items={[
            "unwahre Angaben zu Reichweite, Mitglieder- oder Zuschauerzahlen, Gegenleistungen oder Nachweisen",
            "das künstliche Erhöhen von Scans, Klicks oder Aufrufen",
            "Inhalte, die gegen geltendes Recht verstoßen oder Rechte Dritter verletzen",
            "die Nutzung fremder Konten oder mehrere Konten für dieselbe Organisation",
            "der Versuch, andere Nutzer außerhalb der Plattform zum Vertragsschluss zu bewegen, um die Provision zu umgehen",
            "automatisiertes Auslesen von Profilen oder Kontaktdaten",
          ]}
        />

        <LegalHeading>Rechte an eingestellten Inhalten</LegalHeading>
        <p>
          Die Rechte an eingestellten Inhalten verbleiben beim Nutzer. Er räumt
          MatchGate das nicht ausschließliche, räumlich unbeschränkte Recht ein,
          diese Inhalte im Rahmen des Betriebs der Plattform zu speichern,
          anzuzeigen und technisch aufzubereiten. Eine Nutzung zu Werbezwecken
          außerhalb der Plattform erfolgt nur mit gesonderter Zustimmung.
        </p>

        <LegalHeading>Verfügbarkeit</LegalHeading>
        <p>
          MatchGate ist bemüht, die Plattform durchgehend verfügbar zu halten.
          Wartungsarbeiten, Störungen und Umstände außerhalb unseres
          Einflussbereichs können zu Unterbrechungen führen. Eine bestimmte
          Verfügbarkeit wird nicht zugesichert.
        </p>
      </LegalSection>

      <LegalSection
        id="ranking"
        title="§ 8 MatchScore und Reihenfolge der Ergebnisse"
      >
        <p>
          Suchergebnisse und Vorschläge werden anhand des MatchScore sortiert.
          Er drückt aus, wie stark die Angaben eines Vereins oder Projekts mit
          den Kriterien eines Unternehmens übereinstimmen.
        </p>
        <p>
          Damit nachvollziehbar ist, wie die Reihenfolge entsteht, legt
          MatchGate die wesentlichen Kriterien offen. Maßgeblich sind
          insbesondere Region, Sportart, Zielgruppe, Reichweite, Projektart und
          Budget. Wie stark die einzelnen Kriterien gewichtet werden,
          veröffentlicht MatchGate mit dem Start der Plattform.
        </p>
        <p>
          Eine bevorzugte Platzierung gegen Entgelt gibt es nicht. MatchGate
          bietet auf der Plattform keine eigenen Sponsoringleistungen an und
          behandelt daher keine eigenen Angebote bevorzugt.
        </p>
      </LegalSection>

      <LegalSection id="haftung" title="§ 9 Haftung">
        <p>
          MatchGate haftet unbeschränkt bei Vorsatz und grober Fahrlässigkeit,
          bei Verletzung von Leben, Körper oder Gesundheit sowie nach dem
          Produkthaftungsgesetz.
        </p>
        <p>
          Bei einfacher Fahrlässigkeit haftet MatchGate nur bei Verletzung einer
          wesentlichen Vertragspflicht, deren Erfüllung die ordnungsgemäße
          Durchführung des Vertrags überhaupt erst ermöglicht und auf deren
          Einhaltung der Nutzer regelmäßig vertrauen darf. In diesem Fall ist
          die Haftung auf den vertragstypischen, vorhersehbaren Schaden
          begrenzt.
        </p>

        <LegalHeading>Auswertungen und Kennzahlen</LegalHeading>
        <p>
          Die im Analytics-Dashboard ausgewiesenen Kennzahlen beruhen auf
          technischen Messungen und, soweit externe Quellen einbezogen werden,
          auf Angaben Dritter. MatchGate übernimmt keine Gewähr für deren
          Vollständigkeit und Richtigkeit. Die Kennzahlen dienen der Einordnung
          und begründen keine zugesicherte Eigenschaft der vermittelten
          Sponsoringleistung.
        </p>
        <p>
          Jede Kennzahl ist danach gekennzeichnet, ob sie von MatchGate
          gemessen, von Nutzern angegeben oder aus Angaben hochgerechnet wurde.
          Angaben der Nutzer gibt MatchGate ungeprüft wieder; für ihre
          Richtigkeit sind die jeweiligen Nutzer verantwortlich.
        </p>
        <p>
          Für Zustandekommen, Inhalt und Erfüllung von Sponsoringverträgen
          zwischen Nutzern haftet MatchGate nicht.
        </p>
      </LegalSection>

      <LegalSection id="schluss" title="§ 10 Schlussbestimmungen">
        <p>
          Es gilt das Recht der Bundesrepublik Deutschland unter Ausschluss des
          UN-Kaufrechts.
        </p>
        <p>
          Ausschließlicher Gerichtsstand für alle Streitigkeiten aus diesem
          Vertragsverhältnis ist der Sitz von MatchGate in {COMPANY.city},
          sofern der Nutzer Kaufmann, juristische Person des öffentlichen Rechts oder
          öffentlich-rechtliches Sondervermögen ist.
        </p>
        <p>
          Änderungen dieser Bedingungen teilt MatchGate mindestens 30 Tage vor
          Wirksamwerden in Textform mit. Widerspricht der Nutzer nicht innerhalb
          dieser Frist, gelten die Änderungen als angenommen; auf diese Wirkung
          wird in der Mitteilung gesondert hingewiesen. Bei Widerspruch kann
          jede Seite zum Zeitpunkt des Wirksamwerdens kündigen.
        </p>
        <p>
          Sollte eine Bestimmung unwirksam sein, bleibt die Wirksamkeit der
          übrigen Bestimmungen unberührt.
        </p>
        <p>
          Ergänzend gelten unsere{" "}
          <Link to="/datenschutz" className={linkClass}>
            Datenschutzerklärung
          </Link>{" "}
          und die{" "}
          <Link to="/cookies" className={linkClass}>
            Cookie-Übersicht
          </Link>
          .
        </p>
      </LegalSection>
    </LegalPage>
  );
}
